/**
 * Charger Management — the unified per-charger control surface.
 *
 * Replaces the prior dedicated "Activity Log" screen with a tabbed view
 * that preserves the existing log as the default tab and exposes the new
 * OCPP host-facing endpoints (configuration, smart charging, local auth,
 * firmware, diagnostics, security, raw frames, reservations, remote
 * control).
 *
 * Each tab is self-contained (see `src/ocpp/tabs/*`) so we can iterate on
 * one without touching the others. The tab definitions below double as
 * the navigation contract — adding a new tab is a one-line change here.
 *
 * The activity tab uses a FlatList; other tabs use ScrollViews or static
 * layouts. Do not wrap the activity tab in a vertical ScrollView — that
 * triggers the VirtualizedList-inside-ScrollView warning and breaks list
 * windowing.
 */

import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

import Header from '../components/Header';
import type { RootState } from '../store';
import type { ChargerAttributes } from '../models/charger-model';
import type { RootStackParamList } from '../navigation/stackTypes';

import OcppErrorBanner from './components/OcppErrorBanner';
import type { OcppTabDefinition } from './components/OcppTabBar';
import OcppTabPicker from './components/OcppTabPicker';
import { OcppTheme } from './components/OcppTheme';
import {
  ActivityLogTab,
  ConfigurationTab,
  ConnectorsTab,
  DiagnosticsTab,
  FirmwareTab,
  LocalAuthListTab,
  RawFramesTab,
  RemoteControlTab,
  ReservationsTab,
  SecurityTab,
  SmartChargingTab,
} from './tabs';
import type { OcppTabProps } from './tabs/types';
import { getChargerOcppVersion, isOcppCharger } from './utils/chargerOcppCapabilities';
import type { OcppVersion } from './models/common';

type Props = NativeStackScreenProps<RootStackParamList, 'ChargerManagement'>;

interface TabRecord {
  key: string;
  label: string;
  /** True when the tab requires a recorded ocpp_version on the charger. */
  requiresOcpp?: boolean;
  Component: React.ComponentType<OcppTabProps>;
}

/**
 * Tab catalogue. Order = display order. Add new tabs here; everything
 * else (route, header, gating) updates automatically.
 *
 * The Activity Log tab is intentionally first — it's the existing
 * behaviour and must be the default landing tab so muscle memory keeps
 * working after the rename.
 */
const TAB_CATALOG: TabRecord[] = [
  { key: 'activity', label: 'Activity Log', Component: ActivityLogTab },
  { key: 'connectors', label: 'Connectors', requiresOcpp: true, Component: ConnectorsTab },
  { key: 'remote', label: 'Remote Control', requiresOcpp: true, Component: RemoteControlTab },
  { key: 'config', label: 'Configuration', requiresOcpp: true, Component: ConfigurationTab },
  { key: 'smart', label: 'Smart Charging', requiresOcpp: true, Component: SmartChargingTab },
  { key: 'localAuth', label: 'Local Auth List', requiresOcpp: true, Component: LocalAuthListTab },
  { key: 'firmware', label: 'Firmware', requiresOcpp: true, Component: FirmwareTab },
  { key: 'diagnostics', label: 'Diagnostics', requiresOcpp: true, Component: DiagnosticsTab },
  { key: 'reservations', label: 'Reservations', requiresOcpp: true, Component: ReservationsTab },
  { key: 'security', label: 'Security', requiresOcpp: true, Component: SecurityTab },
  { key: 'rawFrames', label: 'Raw Frames', requiresOcpp: true, Component: RawFramesTab },
];

/** Tab keys whose roots use VirtualizedList (must not sit inside a vertical ScrollView). */
const VIRTUALIZED_TAB_KEYS = new Set<string>(['activity']);

export const CHARGER_MANAGEMENT_TABS = TAB_CATALOG.map((t) => t.key);

const ChargerManagementPage: React.FC<Props> = ({ route }) => {
  const { chargerId, chargerName, initialTab } = route.params ?? ({} as RootStackParamList['ChargerManagement']);

  const myChargers: ChargerAttributes[] | undefined = useSelector(
    (state: RootState) => state.chargers?.myChargers
  );
  const charger = useMemo<ChargerAttributes | null>(
    () => myChargers?.find((c) => c.uuid === chargerId) ?? null,
    [myChargers, chargerId]
  );

  const ocppVersion: OcppVersion | null = useMemo(() => getChargerOcppVersion(charger), [charger]);
  const ocppEnabled = useMemo(() => isOcppCharger(charger), [charger]);

  const visibleTabs = useMemo<TabRecord[]>(() => {
    return TAB_CATALOG.filter((tab) => (tab.requiresOcpp ? ocppEnabled : true));
  }, [ocppEnabled]);

  const [activeKey, setActiveKey] = useState<string>(() => {
    if (initialTab && visibleTabs.some((t) => t.key === initialTab)) return initialTab;
    return visibleTabs[0]?.key ?? 'activity';
  });

  const tabDefinitions: OcppTabDefinition[] = useMemo(
    () => visibleTabs.map((t) => ({ key: t.key, label: t.label })),
    [visibleTabs]
  );

  const Active = visibleTabs.find((t) => t.key === activeKey)?.Component ?? ActivityLogTab;
  const tabBodyUsesVirtualizedList = VIRTUALIZED_TAB_KEYS.has(activeKey);

  if (!chargerId) {
    return (
      <SafeAreaView style={styles.safe}>
        <Header headerText="Charger management" forceBackArrow />
        <View style={styles.missingWrap}>
          <OcppErrorBanner
            error={{
              status: 400,
              title: 'Missing charger',
              message: 'No charger was selected. Go back to My Chargers and choose one.',
              blockedByGate: false,
              featureGated: false,
            }}
          />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <Header headerText="Charger management" forceBackArrow />
      <View style={styles.headerInfo}>
        {chargerName ? (
          <Text style={styles.chargerName} numberOfLines={2}>
            {chargerName}
          </Text>
        ) : null}
        <Text style={styles.chargerMeta}>
          {ocppVersion
            ? `OCPP ${ocppVersion}`
            : ocppEnabled
            ? 'OCPP charger'
            : 'Limited controls — non-OCPP charger.'}
        </Text>
        <View style={styles.pickerRow}>
          <OcppTabPicker tabs={tabDefinitions} activeKey={activeKey} onSelect={setActiveKey} />
        </View>
      </View>

      {tabBodyUsesVirtualizedList ? (
        <View style={styles.body}>
          {/* Flex required so FlatList descendants get a real height (padding-only View collapses to 0). */}
          <View style={[styles.bodyContent, styles.bodyContentFlexFill]}>
            <Active
              chargerId={chargerId}
              chargerName={chargerName ?? ''}
              charger={charger}
              ocppVersion={ocppVersion}
              isActive={true}
            />
          </View>
        </View>
      ) : (
        <ScrollView
          style={styles.body}
          contentContainerStyle={styles.bodyContent}
          showsVerticalScrollIndicator
        >
          <Active
            chargerId={chargerId}
            chargerName={chargerName ?? ''}
            charger={charger}
            ocppVersion={ocppVersion}
            isActive={true}
          />
        </ScrollView>
      )}
    </SafeAreaView>
  );
};

export default ChargerManagementPage;

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: OcppTheme.bg,
  },
  headerInfo: {
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 10,
  },
  pickerRow: {
    marginTop: 10,
  },
  chargerName: {
    color: OcppTheme.textPrimary,
    fontSize: 20,
    fontWeight: '700',
  },
  chargerMeta: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginTop: 4,
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    padding: 12,
    paddingBottom: 40,
  },
  bodyContentFlexFill: {
    flex: 1,
  },
  missingWrap: {
    padding: 16,
  },
});
